import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import {
  buildResourceSetFieldRows,
  type ResourceSetFieldRow,
  type ResourceSetFieldSection,
} from './resource-set-fields';

type ResourceSetViewModalProps = {
  resourceSet: ResourceSet | null;
  aorNameById?: ReadonlyMap<string, string>;
  onClose: () => void;
  onEdit?: (resourceSet: ResourceSet) => void;
};

const SECTIONS: ResourceSetFieldSection[] = ['Details', 'More fields'];

/** Read-only view of every field stored on an asset (resource set). */
export function ResourceSetViewModal({
  resourceSet,
  aorNameById,
  onClose,
  onEdit,
}: ResourceSetViewModalProps) {
  const rows = useMemo<ResourceSetFieldRow[]>(
    () =>
      resourceSet
        ? buildResourceSetFieldRows(resourceSet, { aorNameById })
        : [],
    [aorNameById, resourceSet]
  );

  return (
    <Dialog
      open={!!resourceSet}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="dark tw:flex tw:max-h-[85vh] tw:flex-col tw:gap-3 tw:sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{resourceSet?.name || 'Asset'}</DialogTitle>
          <DialogDescription>
            {[resourceSet?.kind, resourceSet?.type]
              .filter(Boolean)
              .join(' · ') || 'Asset details'}
          </DialogDescription>
        </DialogHeader>
        <div className="tw:min-h-0 tw:flex-1 tw:space-y-4 tw:overflow-y-auto tw:pr-1">
          {SECTIONS.map((section) => {
            const sectionRows = rows.filter((row) => row.section === section);
            if (sectionRows.length === 0) return null;
            return (
              <section key={section} className="tw:space-y-1.5">
                <h3 className="tw:m-0 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                  {section}
                </h3>
                <dl className="tw:m-0 tw:grid tw:grid-cols-[minmax(8rem,auto)_1fr] tw:gap-x-4 tw:gap-y-1.5 tw:text-sm">
                  {sectionRows.map((row) => (
                    <div key={row.key} className="tw:contents">
                      <dt className="tw:text-muted-foreground">{row.label}</dt>
                      <dd className="tw:m-0 tw:break-words tw:whitespace-pre-wrap">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}
        </div>
        <DialogFooter>
          {resourceSet && onEdit ? (
            <Button type="button" onClick={() => onEdit(resourceSet)}>
              Edit
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
